import React from 'react';
import ReactDOM from 'react-dom';
import { Button, Modal } from './ui';
import type { ButtonVariant } from './ui';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const variantConfig: Record<NonNullable<ConfirmDialogProps['variant']>, {
  eyebrow: string;
  confirmVariant: ButtonVariant;
}> = {
  danger:  { eyebrow: 'Confirm action', confirmVariant: 'destructive' },
  warning: { eyebrow: 'Heads up',       confirmVariant: 'spark' },
  info:    { eyebrow: 'Confirm',        confirmVariant: 'primary' },
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'warning',
}) => {
  if (!isOpen) return null;

  const config = variantConfig[variant];

  return ReactDOM.createPortal(
    <Modal
      open
      onClose={onCancel}
      eyebrow={config.eyebrow}
      title={title}
      width={440}
      variant="compact"
      ariaLabel={title}
      footer={
        <>
          <Button variant="ghost" size="md" onClick={onCancel} aria-label="Cancel">
            {cancelText}
          </Button>
          <Button
            variant={config.confirmVariant}
            size="md"
            onClick={onConfirm}
            autoFocus
            aria-label={`${confirmText} - ${variant} action`}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <p style={{ fontSize: 14, color: 'var(--fg-3)', lineHeight: 1.6, margin: 0 }}>
        {message}
      </p>
    </Modal>,
    document.body,
  );
};

export default ConfirmDialog;
